import type {
  GpbDefinition, GpbGoal, GpbPredicate, GpbQuest, GpbRank,
} from '../../types/gpb';

/**
 * ⭐⭐ **WHAT THE ENGINE WOULD MISREAD, REFUSED AT AUTHORING TIME.**
 *
 * Athlete was translated by hand into a TypeScript const, so the compiler caught
 * its shape and a human caught its sense. Builds 2–7 arrive as data, where
 * neither applies: a predicate naming a habit that does not exist type-checks
 * perfectly, because the id is just a string.
 *
 * ⚠️⚠️ **THE DANGEROUS DEFECTS ARE THE ONES THAT COMPLETE, NOT THE ONES THAT
 * CRASH.** `progress(have, need)` reports `done: have >= need`, so a need of ZERO
 * is satisfied by nothing at all. A goal with no predicates, a named set with no
 * ids, a `tasks` predicate on a project with no tasks — each is instantly and
 * permanently complete, hands out its rank, and looks like generosity rather than
 * a bug. Those three are the reason this file exists.
 *
 * ⚠️ Returns defects; never throws. An authoring surface wants every problem at
 * once, not the first one.
 *
 * ⚠️ This validates a DEFINITION, not the records it will create. `interval`,
 * `linkStartedAt` and the rest belong to the writer at Apply time and are not
 * authored here — validating a field the schema does not carry would be inventing
 * a contract rather than enforcing one.
 */

export interface GpbDefect {
  /** Stable, so callers can group and tests can assert without matching prose. */
  code: GpbDefectCode;
  /**
   * ⚠️⚠️ **A DEFECT IS A MISREADING; A GAP IS AN HONEST ABSENCE.**
   *
   * Found by running this against ATHLETE, which failed nine times — every one of
   * them a predicate naming a program that has not been translated yet. That is
   * not a mistake: the schema says so in `GpbComponents` ("Only programs that have
   * been TRANSLATED appear here"), and `readEvidence` already reports those as
   * `{ kind: 'unbound' }` rather than counting them wrongly. A validator that
   * refused the only real profile would be wrong about the schema, not about the
   * profile.
   *
   * So `defect` means the engine would read this definition incorrectly, and
   * `gap` means the engine reads it correctly as not-yet-available. Only defects
   * block publishing.
   */
  severity: 'defect' | 'gap';
  /** Authored location — `R1/Q01/G02`, or `components.habits[H09]`. */
  where: string;
  /** What is wrong, naming the offending id. */
  message: string;
}

export type GpbDefectCode =
  | 'always-complete'
  | 'untranslated-program'
  | 'missing-component'
  | 'unknown-member'
  | 'duplicate-id'
  | 'bad-cardinality'
  | 'release-mismatch'
  | 'broken-chain'
  | 'missing-domain'
  | 'blank-field'
  /** ⚠️ The PRODUCT contract (seven ranks, the seven-tier ability) — publication only;
   *  produced by `publicationContract.ts`, never by `validateDefinition`. */
  | 'contract';

const blank = (v: string | undefined): boolean => !v || !v.trim();

/** ⚠️ Every id a predicate may name, gathered once so lookups cannot disagree. */
interface Known {
  habits: Set<string>;
  programs: Map<string, Set<string>>;   // program id -> its occurrence ids
  projects: Map<string, number>;        // project id -> its task count
  skills: Set<string>;
}

function knownOf(def: GpbDefinition): Known {
  return {
    habits: new Set((def.components?.habits ?? []).map(h => h.id)),
    programs: new Map((def.components?.programs ?? []).map(
      p => [p.id, new Set(p.occurrences.map(o => o.id))] as const,
    )),
    projects: new Map((def.projects ?? []).map(p => [p.id, p.tasks.length] as const)),
    skills: new Set((def.components?.ability?.skills ?? []).map(s => s.id)),
  };
}

/**
 * ⚠️⚠️ A predicate that asks for NOTHING is satisfied by nothing. Checked before
 * anything else about the predicate, because it is the only defect here that
 * silently hands out a rank instead of failing to count.
 */
function checkAlwaysComplete(
  pred: GpbPredicate, known: Known, where: string, out: GpbDefect[],
): void {
  const zero = (what: string) => out.push({
    code: 'always-complete', severity: 'defect', where,
    message: `${what} asks for nothing, so it is complete before anybody does anything.`,
  });

  switch (pred.kind) {
    case 'distinct_dates':
      if (!(pred.count > 0)) zero(`\`distinct_dates\` on ${pred.habit} with count ${pred.count}`);
      break;
    case 'occurrences':
      if (pred.ids.length === 0) zero(`\`occurrences\` on ${pred.program} with an empty id list`);
      break;
    case 'skills':
      if (pred.ids.length === 0) zero('`skills` with an empty id list');
      break;
    case 'tasks':
      /* ⚠️ The denominator comes from the PROJECT, so an empty project makes this
         complete — and the predicate itself looks perfectly well formed. */
      if (known.projects.get(pred.project) === 0) {
        zero(`\`tasks\` on project ${pred.project}, which has no tasks`);
      }
      break;
    case 'record':
      if (pred.fields.length === 0) zero(`\`record\` ${pred.record} with no required fields`);
      break;
  }
}

/** Every id a predicate names must exist, and belong to the thing it names. */
function checkReferences(
  pred: GpbPredicate, known: Known, where: string, out: GpbDefect[],
): void {
  switch (pred.kind) {
    case 'distinct_dates':
      if (!known.habits.has(pred.habit)) {
        out.push({ code: 'missing-component', severity: 'defect', where,
          message: `names habit ${pred.habit}, which is not in \`components.habits\`.` });
      }
      break;

    case 'occurrences': {
      const occ = known.programs.get(pred.program);
      if (!occ) {
        /* ⚠️⚠️ A GAP, NOT A DEFECT — and ATHLETE trips it nine times on purpose.
           Only translated programs appear in `components.programs`; a predicate
           may name one that is not, and `readEvidence` reports it as `unbound`
           rather than counting it wrongly. Blocking publication on this would
           make the only real profile unpublishable. */
        out.push({ code: 'untranslated-program', severity: 'gap', where,
          message: `names program ${pred.program}, which is not translated yet — `
            + 'this goal will read as unbound until it is.' });
        break;
      }
      /* ⚠️ A program that exists is not enough — the occurrence has to be one of
         ITS occurrences, or the goal counts evidence that can never arrive. */
      for (const id of pred.ids) {
        if (!occ.has(id)) {
          out.push({ code: 'unknown-member', severity: 'defect', where,
            message: `names occurrence ${id}, which program ${pred.program} does not author.` });
        }
      }
      break;
    }

    case 'skills':
      for (const id of pred.ids) {
        if (!known.skills.has(id)) {
          out.push({ code: 'unknown-member', severity: 'defect', where,
            message: `names skill ${id}, which the ability does not author.` });
        }
      }
      break;

    case 'tasks':
      if (!known.projects.has(pred.project)) {
        out.push({ code: 'missing-component', severity: 'defect', where,
          message: `names project ${pred.project}, which the definition does not author.` });
      }
      break;

    case 'record':
      /* A record has no component to reference — its id IS the form's name. */
      if (blank(pred.record)) {
        out.push({ code: 'blank-field', severity: 'defect', where, message: '`record` has no name.' });
      }
      break;
  }
}

/**
 * ⚠️⚠️ **AN `after` CHAIN THAT CANNOT RESOLVE FAILS CLOSED AT RUNTIME** —
 * `startsFrom` returns null and the goal reads as 0 of 1 for ever, which looks
 * like unmet work rather than a broken definition. A cycle does the same via its
 * `seen` guard. Both are authoring mistakes and belong here, where they can still
 * be fixed.
 */
function checkChains(goal: GpbGoal, where: string, out: GpbDefect[]): void {
  const byRef = new Map<string, GpbPredicate>();
  for (const p of goal.all) if (p.ref) byRef.set(p.ref, p);

  for (const pred of goal.all) {
    if (!pred.after) continue;

    if (!byRef.has(pred.after)) {
      out.push({ code: 'broken-chain', severity: 'defect', where,
        message: `\`after\` names ${pred.after}, which no predicate in this goal declares as its \`ref\`.` });
      continue;
    }

    /* Walk the chain; a repeat is a cycle. */
    const seen = new Set<string>(pred.ref ? [pred.ref] : []);
    let at: GpbPredicate | undefined = byRef.get(pred.after);
    while (at?.after) {
      if (at.ref && seen.has(at.ref)) break;
      if (at.ref) seen.add(at.ref);
      const next: GpbPredicate | undefined = byRef.get(at.after);
      if (!next) break;
      if (next.ref && seen.has(next.ref)) {
        out.push({ code: 'broken-chain', severity: 'defect', where,
          message: `\`after\` chain through ${next.ref} forms a cycle, so it can never resolve.` });
        break;
      }
      at = next;
    }
  }
}

function checkGoal(goal: GpbGoal, known: Known, where: string, out: GpbDefect[]): void {
  if (blank(goal.id)) out.push({ code: 'blank-field', severity: 'defect', where, message: 'goal has no id.' });
  if (blank(goal.title)) out.push({ code: 'blank-field', severity: 'defect', where, message: `goal ${goal.id} has no title.` });
  if (blank(goal.evidenceLine)) {
    out.push({ code: 'blank-field', severity: 'defect', where, message: `goal ${goal.id} has no \`evidenceLine\`.` });
  }

  /* ⚠️⚠️ A goal with no parts reports 0 of 0 — complete, for everybody, always. */
  if (goal.all.length === 0) {
    out.push({ code: 'always-complete', severity: 'defect', where,
      message: `goal ${goal.id} has no predicates, so it is complete before anybody does anything.` });
    return;
  }

  const refs = new Set<string>();
  goal.all.forEach((pred, i) => {
    const at = `${where}#${i}`;
    if (pred.ref) {
      if (refs.has(pred.ref)) {
        out.push({ code: 'duplicate-id', severity: 'defect', where: at,
          message: `two predicates share the ref ${pred.ref}, so \`after\` cannot name one of them.` });
      }
      refs.add(pred.ref);
    }
    checkAlwaysComplete(pred, known, at, out);
    checkReferences(pred, known, at, out);
  });

  checkChains(goal, where, out);
}

function checkQuest(quest: GpbQuest, known: Known, where: string, out: GpbDefect[]): void {
  if (blank(quest.id)) out.push({ code: 'blank-field', severity: 'defect', where, message: 'quest has no id.' });
  if (blank(quest.title)) out.push({ code: 'blank-field', severity: 'defect', where, message: `quest ${quest.id} has no title.` });

  if (quest.goals.length === 0) {
    out.push({ code: 'always-complete', severity: 'defect', where,
      message: `quest ${quest.id} has no goals, so it is complete before anybody does anything.` });
  }

  /* ⚠️⚠️ UNIQUE WITHIN THE QUEST, NOT THE DEFINITION. Two quests may each author a
     `G01`; `releaseDiff` keys on `questId/goalId` for exactly this reason. A
     duplicate INSIDE one quest is the real defect — it makes two requirements
     indistinguishable to every surface that reads them. */
  const seen = new Set<string>();
  for (const goal of quest.goals) {
    if (seen.has(goal.id)) {
      out.push({ code: 'duplicate-id', severity: 'defect', where: `${where}/${goal.id}`,
        message: `quest ${quest.id} authors goal ${goal.id} twice.` });
    }
    seen.add(goal.id);
    checkGoal(goal, known, `${where}/${goal.id}`, out);
  }
}

function checkRank(rank: GpbRank, known: Known, out: GpbDefect[]): void {
  const where = `R${rank.rank}`;

  if (!Number.isInteger(rank.rank) || rank.rank < 1 || rank.rank > 7) {
    out.push({ code: 'bad-cardinality', severity: 'defect', where,
      message: `rank ${rank.rank} is outside 1–7; a build has seven ranks (D6).` });
  }
  if (blank(rank.title)) out.push({ code: 'blank-field', severity: 'defect', where, message: 'rank has no title.' });

  /* ⚠️ The package fixes this cardinality: one to three quests per rank. */
  if (rank.quests.length < 1 || rank.quests.length > 3) {
    out.push({ code: 'bad-cardinality', severity: 'defect', where,
      message: `rank ${rank.rank} has ${rank.quests.length} quests; the package authors one to three.` });
  }

  const seen = new Set<string>();
  for (const quest of rank.quests) {
    if (seen.has(quest.id)) {
      out.push({ code: 'duplicate-id', severity: 'defect', where: `${where}/${quest.id}`,
        message: `rank ${rank.rank} authors quest ${quest.id} twice.` });
    }
    seen.add(quest.id);
    checkQuest(quest, known, `${where}/${quest.id}`, out);
  }
}

/**
 * ⚠️⚠️ **A COMPONENT WITHOUT A DOMAIN CANNOT BE CREATED.** Every record the Apply
 * writes carries its execution domain, and the writer has nowhere to get one from
 * if the definition does not supply it.
 */
function checkComponents(def: GpbDefinition, out: GpbDefect[]): void {
  const dup = (kind: string, ids: readonly string[]) => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) {
        out.push({ code: 'duplicate-id', severity: 'defect', where: `components.${kind}[${id}]`,
          message: `${kind} id ${id} is authored twice.` });
      }
      seen.add(id);
    }
  };

  const habits = def.components?.habits ?? [];
  dup('habits', habits.map(h => h.id));
  for (const h of habits) {
    const where = `components.habits[${h.id}]`;
    if (blank(h.id)) out.push({ code: 'blank-field', severity: 'defect', where, message: 'habit has no id.' });
    if (blank(h.title)) out.push({ code: 'blank-field', severity: 'defect', where, message: `habit ${h.id} has no title.` });
    if (blank(h.domain)) {
      out.push({ code: 'missing-domain', severity: 'defect', where,
        message: `habit ${h.id} has no domain, so the record cannot be created.` });
    }
    /* The app requires a description; the package calls it the behaviour. */
    if (blank(h.behaviour)) {
      out.push({ code: 'blank-field', severity: 'defect', where, message: `habit ${h.id} has no \`behaviour\`.` });
    }
  }

  const ability = def.components?.ability;
  if (ability) {
    if (blank(ability.domain)) {
      out.push({ code: 'missing-domain', severity: 'defect', where: `components.ability[${ability.id}]`,
        message: `ability ${ability.id} has no domain, so the record cannot be created.` });
    }
    dup('ability.skills', ability.skills.map(s => s.id));
    for (const s of ability.skills) {
      if (blank(s.title)) {
        out.push({ code: 'blank-field', severity: 'defect', where: `components.ability.skills[${s.id}]`,
          message: `skill ${s.id} has no title.` });
      }
    }
  }

  const programs = def.components?.programs ?? [];
  dup('programs', programs.map(p => p.id));
  for (const p of programs) {
    const where = `components.programs[${p.id}]`;
    if (blank(p.domain)) {
      out.push({ code: 'missing-domain', severity: 'defect', where,
        message: `program ${p.id} has no domain, so its modules cannot be created.` });
    }
    if (p.occurrences.length === 0) {
      out.push({ code: 'always-complete', severity: 'defect', where,
        message: `program ${p.id} authors no occurrences, so any goal naming it counts nothing.` });
    }
    /* ⚠️⚠️ ONE MODULE PER OCCURRENCE — the binding is 1:1 BY CONSTRUCTION, and two
       occurrences sharing an id makes their completions indistinguishable, which
       is the one ambiguity the evidence reader refuses to guess through. Several
       occurrences SHARING A CARD is expected and fine; sharing an ID is not. */
    dup(`programs[${p.id}].occurrences`, p.occurrences.map(o => o.id));
    for (const o of p.occurrences) {
      if (blank(o.card)) {
        out.push({ code: 'blank-field', severity: 'defect', where: `${where}.occurrences[${o.id}]`,
          message: `occurrence ${o.id} names no card.` });
      }
    }
  }

  const projects = def.projects ?? [];
  dup('projects', projects.map(p => p.id));
  for (const p of projects) {
    const where = `projects[${p.id}]`;
    dup(`projects[${p.id}].tasks`, p.tasks.map(t => t.id));
    for (const t of p.tasks) {
      /* ⚠️ A task's TITLE is what a person reads on their own Projects page. The
         schema once carried ids alone, and fifteen tasks shipped called `J01-T01`. */
      if (blank(t.title) || t.title === t.id) {
        out.push({ code: 'blank-field', severity: 'defect', where: `${where}.tasks[${t.id}]`,
          message: `task ${t.id} has no title of its own.` });
      }
    }
  }
}

/**
 * Validate a definition. Empty result means the engine can read it.
 *
 * @param publishedVersions Versions already published for this id, when known.
 *   Only then can `release.supersedes` be checked — an authoring surface has the
 *   list; a unit test usually does not.
 */
export function validateDefinition(
  def: GpbDefinition,
  publishedVersions?: readonly string[],
): readonly GpbDefect[] {
  const out: GpbDefect[] = [];
  const known = knownOf(def);

  for (const [field, value] of [['id', def.id], ['name', def.name], ['version', def.version],
    ['author', def.author], ['promise', def.promise]] as const) {
    if (blank(value)) {
      out.push({ code: 'blank-field', severity: 'defect', where: 'definition', message: `definition has no \`${field}\`.` });
    }
  }

  /* ⚠️⚠️ A release entry describing a different version than the one it ships
     inside is a changelog confidently describing the wrong release (H4). */
  if (!def.release) {
    out.push({ code: 'release-mismatch', severity: 'defect', where: 'release',
      message: 'definition has no release entry, so an update could not say what it changed.' });
  } else {
    if (def.release.version !== def.version) {
      out.push({ code: 'release-mismatch', severity: 'defect', where: 'release',
        message: `release names v${def.release.version} but the definition is v${def.version}.` });
    }
    if (blank(def.release.publishedOn)) {
      out.push({ code: 'blank-field', severity: 'defect', where: 'release', message: 'release has no `publishedOn`.' });
    }
    if (def.release.supersedes !== undefined) {
      if (def.release.supersedes === def.version) {
        out.push({ code: 'release-mismatch', severity: 'defect', where: 'release',
          message: `release supersedes v${def.release.supersedes}, which is itself.` });
      } else if (publishedVersions && !publishedVersions.includes(def.release.supersedes)) {
        out.push({ code: 'release-mismatch', severity: 'defect', where: 'release',
          message: `release supersedes v${def.release.supersedes}, which was never published.` });
      }
    }
  }

  checkComponents(def, out);

  const ranks = def.ranks ?? [];
  if (ranks.length === 0) {
    out.push({ code: 'bad-cardinality', severity: 'defect', where: 'ranks', message: 'definition authors no ranks.' });
  }
  const seenRanks = new Set<number>();
  for (const rank of ranks) {
    if (seenRanks.has(rank.rank)) {
      out.push({ code: 'duplicate-id', severity: 'defect', where: `R${rank.rank}`,
        message: `rank ${rank.rank} is authored twice.` });
    }
    seenRanks.add(rank.rank);
    checkRank(rank, known, out);
  }

  return out;
}

/**
 * ⚠️ GAPS DO NOT BLOCK. Athlete ships with nine and is the profile the whole
 * engine was built against — refusing it would be the validator being wrong.
 */
export const defectsOnly = (found: readonly GpbDefect[]): readonly GpbDefect[] =>
  found.filter(f => f.severity === 'defect');

/** Convenience for a caller that only needs the verdict. */
export const isValidDefinition = (def: GpbDefinition): boolean =>
  defectsOnly(validateDefinition(def)).length === 0;
